import { Flex } from 'lib/components/styles/layout';
import React from 'react';
import { Typography } from 'react-covideo-common';
import {
  getInitialLandingPageValuesForBulkEdit,
  ILandingPageBuilderBulkEditFormData,
} from '../../../helpers/getInitialLandingPageValuesForBulkEdit';
import { ChangedFieldsSection } from './ChangedFieldsSection';
import { ChangesSummaryForm } from '../../../hooks/useChangesSummaryMapper';
import { IImgProps } from 'lib/components/formik/FormikImageUpload/types';

interface IChangedFieldsFormProps {
  form: ChangesSummaryForm;
  values: ILandingPageBuilderBulkEditFormData;
}

export const ChangedFieldsForm = ({
  form,
  values,
}: IChangedFieldsFormProps) => {
  // Filter and render sections that have at least one valid field
  const filteredSections = form.sections
    .map(section => {
      const changedFields = section.fields
        .filter(field => {
          if (!field.name) return false;
          const initialValues = getInitialLandingPageValuesForBulkEdit();

          const fieldName =
            field.name as keyof ILandingPageBuilderBulkEditFormData;
          const currentValue = values[fieldName];
          const initialValue = initialValues[fieldName];

          // Skip fields where both current and initial values are null/undefined
          if (currentValue == null && initialValue == null) return false;

          // Special handling for image fields
          if (['headerLogo', 'bodyBackgroundImage'].includes(field.name)) {
            const isEmptyImage = (val: IImgProps) =>
              typeof val === 'object' &&
              val !== null &&
              val.file === null &&
              val.url === '';

            if (
              isEmptyImage(currentValue as IImgProps) &&
              isEmptyImage(initialValue as IImgProps)
            ) {
              return false;
            }

            return (
              JSON.stringify(currentValue) !== JSON.stringify(initialValue)
            );
          }

          // Default comparison for other fields
          return currentValue !== initialValue;
        })
        .map(field => {
          const fieldName =
            field.name as keyof ILandingPageBuilderBulkEditFormData;
          return field.jsx(values[fieldName]);
        })
        .filter(Boolean) as JSX.Element[];

      if (changedFields.length === 0) return null;

      return (
        <ChangedFieldsSection
          key={section.title}
          section={section}
          fields={changedFields}
          values={values}
        />
      );
    })
    .filter(Boolean); // Remove null sections

  // Don't render the form at all if it has no sections with visible fields
  if (filteredSections.length === 0) return null;

  return (
    <Flex key={form.form} style={{ width: '100%' }} gap='24px'>
      <Typography variant='subheading' fontWeight={600}>
        {form.title}
      </Typography>
      <Flex gap='20px'>{filteredSections}</Flex>
    </Flex>
  );
};
